import { inject, Injectable } from '@angular/core';
import { catchError, Observable, throwError } from 'rxjs';
import { concatMap } from 'rxjs/operators';
import { OidcStorageService } from '../storage/oidc-storage.service';
import { LoggerWrapper } from '../../shared/providers/logger/logger-wrapper.service';
import { LoggerFactory } from '../../shared/providers/logger/logger-factory.service';
import { CallbackContext } from './callback-context';
import { UserCallbackHandlerService } from './callback-handling/user-callback-handler.service';
import { RefreshSessionCallbackHandlerService } from './callback-handling/refresh-session-callback-handler.service';
import { RefreshTokenCallbackHandlerService } from './callback-handling/refresh-token-callback-handler.service';
import { StateValidationCallbackHandlerService } from './callback-handling/state-validation-callback-handler.service';
import { OpenIdConfiguration } from '../oidc-security.service';

@Injectable({ providedIn: 'root' })
export class RefreshSessionService {
  private logger: LoggerWrapper = inject(LoggerFactory).getInstance(this);

  constructor(
    private readonly oidcStorageService: OidcStorageService,
    private readonly userHandlerService: UserCallbackHandlerService,
    private readonly refreshSessionCallbackHandlerService: RefreshSessionCallbackHandlerService,
    private readonly refreshTokenCallbackHandlerService: RefreshTokenCallbackHandlerService,
    private readonly stateValidationCallbackHandlerService: StateValidationCallbackHandlerService
  ) {}

  forceRefreshSession(config: OpenIdConfiguration): Observable<any> {
    return this.processRefreshToken(config).pipe(
      catchError(async (error) => {
        this.logger.error(error, '[oidc] forceRefreshSession');
        await this.oidcStorageService.clear(config.type);

        return throwError(() => new Error(error));
      })
    );
  }

  private processRefreshToken(config: OpenIdConfiguration): Observable<CallbackContext> {
    return this.refreshSessionCallbackHandlerService.retrieveRefreshToken(config).pipe(
      concatMap((callbackContext) =>
        this.refreshTokenCallbackHandlerService.refreshSessionWithRefreshToken(callbackContext, config)
      ),
      concatMap((callbackContext) =>
        this.stateValidationCallbackHandlerService.validateAndSaveSessionTokens(callbackContext, config)
      ),
      concatMap((callbackContext) => this.userHandlerService.saveUserData(callbackContext, config))
    );
  }
}
